import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../Lists.module.css';
import DoublyLinkedListView from '../listengine/DoublyLinkedListEngine';
import { ListCommand } from '../../../types/ListCommands';


const DOUBLY_LINKED_LIST_CODE = `class Node {
  constructor(value) {
    this.value = value;
    this.next = null;
    this.prev = null; // The new pointer
  }
}
class DoublyLinkedList {
  insertAt(value, index) { }
  deleteAt(index) { }
}`;

const MAX_LIST_SIZE = 10;

const DoublyLinkedListPage: React.FC = () => {
  const navigate = useNavigate();
  const [command, setCommand] = useState<ListCommand | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [inputIndex, setInputIndex] = useState('');
  const [activeLine, setActiveLine] = useState<number | null>(null);
  const [list, setList] = useState<{ value: number }[]>([
    { value: 12 }, { value: 99 }, { value: 37 }, { value: 59 }, { value: 22 }
  ]);

  const handleInsert = () => {
    if (isAnimating || inputValue === '' || inputIndex === '') return;
    setIsAnimating(true);
    setActiveLine(11); 
    setCommand({
      id: Date.now(),
      action: 'insertAt',
      value: parseInt(inputValue),
      index: parseInt(inputIndex),
    });
    setInputValue('');
    setInputIndex('');
  };
  
  const handleDelete = () => {
    if (isAnimating || inputIndex === '') return;
    setIsAnimating(true);
    setActiveLine(12); 
    setCommand({ id: Date.now(), action: 'removeAt', index: parseInt(inputIndex) });
    setInputIndex('');
  };
  
  const handleGenerateRandom = () => {
    const randomValue = Math.floor(Math.random() * 100);
    const randomIndex = list.length > 0 ? Math.floor(Math.random() * list.length) : 0;
    setInputValue(randomValue.toString());
    setInputIndex(randomIndex.toString());
  };

  const handleAnimationComplete = () => {
    setIsAnimating(false);
    setActiveLine(null);
  };
  const handleReset = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCommand({ id: Date.now(), action: 'reset' });
  };

  return (
    <div className={styles.container}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>&larr; Return</button>
      <h1 className={styles.title}>Doubly Linked List</h1>
      
        <div className={styles.description}>
          <h2>Description</h2>
          <p>A Doubly Linked List is an enhanced linked list where each node has a pointer to the <strong>next</strong> node and a pointer to the <strong>previous</strong> node. This allows for traversal in both directions (forwards and backwards).</p>
        </div>
        <div className={styles.useCase}>
          <h2>Use Cases</h2>
          <p>Ideal for creating "undo/redo" functionality, browser caches for "back" and "forward" navigation, or certain types of task schedulers.</p>
        </div>
        <div className={styles.timeComplexity}>
          <h2>Time Complexity</h2>
          <p>Access and Search are still <strong>O(n)</strong>. However, if you already have a pointer to a node, Deletion becomes an incredibly fast <strong>O(1)</strong> operation.</p>
        </div>

        <div className={styles.mainContent}>
        <div style={{flex: 1}}>
          <DoublyLinkedListView command={command} onComplete={handleAnimationComplete} />
        </div>
        <div className={styles.controlsAndCode}>
            <div className={styles.controls}>
                <input type="number" value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder="Value" disabled={isAnimating} />
                <input type="number" value={inputIndex} onChange={e => setInputIndex(e.target.value)} placeholder="Index" disabled={isAnimating} />
                <button onClick={handleInsert} disabled={isAnimating}>Insert At</button>
                <button onClick={handleDelete} disabled={isAnimating}>Delete At</button>
                <button onClick={handleGenerateRandom} disabled={isAnimating}>Generate</button>
                <button onClick={handleReset} disabled={isAnimating}>Reset</button>

            </div>
            <div className={styles.codeDisplay}>
                {DOUBLY_LINKED_LIST_CODE.split('\n').map((line, index) => (
                  <div key={index} className={`${styles.codeLine} ${activeLine === index ? styles.highlightedCode : ''}`}>
                    {line || ' '}
                  </div>
                ))}
            </div>
        </div>
      </div>
    </div>
  );
};

export default DoublyLinkedListPage;